import { useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { OneDataCollection, useDataCollectionSource } from '@factorialco/f0-react/dist/experimental'
import {
  PerformanceProcessAgreementDocumentCardEsignaturesFragment,
  PerformanceProcessAgreementDocumentCardFragment,
  PerformanceProcessAgreementDocumentCardGenerateDocumentDocument,
  PerformanceProcessTargetAgreementCardEsignaturesFragment,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import buildUrl from 'modules/core/lib/buildUrl'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import DocumentModal from 'modules/documents/components/DocumentModal'
import {
  agreementStatus,
  agreementStatusLabel,
  agreementStatusToVariant,
} from 'modules/performance_process/components/agreements/StatusTag'

export const getSignaturesCompletionLabel = (
  esignatures:
    | PerformanceProcessAgreementDocumentCardEsignaturesFragment[]
    | PerformanceProcessTargetAgreementCardEsignaturesFragment[]
): string => {
  return i18n.t('performance_process.agreements.agreement_list_card.signatures', {
    count: esignatures.filter((signature) => signature.status === 'signed').length,
    total: esignatures.length === 0 ? 2 : esignatures.length,
  })
}

interface Props {
  agreement: PerformanceProcessAgreementDocumentCardFragment
  esignatures: PerformanceProcessAgreementDocumentCardEsignaturesFragment[]
}

export const AgreementDocumentCard = ({ agreement, esignatures }: Props) => {
  const [openPreview, setOpenPreview] = useState(false)
  const { access } = useCurrentSession()

  const [generateDocument, { loading: generateDocumentLoading }] = useMutation(
    PerformanceProcessAgreementDocumentCardGenerateDocumentDocument,
    {
      extractorFn: (data) => data.performanceMutations.generateDocumentPerformanceAgreement,
    }
  )

  const agreementDocument = agreement.agreementDocuments[0]
  const fileUrl = agreementDocument?.fileUrl
  const document = agreementDocument?.document
  const signatureStatus = agreementDocument?.signatureStatus

  const generating = agreement.generatingDocument || generateDocumentLoading
  const canGenerate = agreement._policies?.generateDocument?.can && !generating

  const detailLabels = compact([
    signatureStatus && getSignaturesCompletionLabel(esignatures),
    agreementDocument?.createdAt &&
      i18n.t('performance_process.agreements.agreement.document_card.generated_on', {
        date: i18nDateToString(stringToDate(agreementDocument.createdAt, 'backend_datetime'), 'ui'),
      }),
  ])

  const signatureRemoteUrl = esignatures.find(
    (signature) => signature.pending && signature.signee.id === access.id
  )?.remoteUrl

  const canSign =
    (signatureStatus === 'pending' || signatureStatus === 'partially_signed') &&
    (esignatures.length === 0 || !!signatureRemoteUrl)

  const onGenerateDocument = async () => {
    const result = await generateDocument({ variables: { id: agreement.id } })
    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('performance_process.agreements.agreement.document_card.success'),
      })
    }
  }

  const dataSource = useDataCollectionSource({
    dataAdapter: {
      fetchData: async () => ({ records: [agreement] }),
    },
    itemOnClick: () => () => {
      if (document) return setOpenPreview(true)
      if (canGenerate) return onGenerateDocument()
    },
    itemActions:
      canGenerate || fileUrl
        ? () => {
            const actions = []

            if (canGenerate) {
              actions.push({
                label: i18n.t(
                  'performance_process.agreements.agreement.document_card.generate_pdf'
                ),
                onClick: onGenerateDocument,
                type: 'primary' as const,
              })
            }

            if (canSign) {
              actions.push({
                label: i18n.t('common.sign'),
                type: 'primary' as const,
                onClick: () =>
                  signatureRemoteUrl
                    ? window.location.assign(signatureRemoteUrl)
                    : setOpenPreview(true),
              })
            }

            if (fileUrl) {
              actions.push({
                label: i18n.t('documents.download'),
                type: canSign ? ('secondary' as const) : ('primary' as const),
                onClick: () => window.open(buildUrl(agreementDocument.fileUrl), '_blank'),
              })
            }

            return actions
          }
        : undefined,
  })

  return (
    <Box cursor={document || canGenerate ? 'pointer' : 'default'}>
      <OneDataCollection
        source={dataSource}
        visualizations={[
          {
            type: 'list',
            options: {
              fields: [
                {
                  label: i18n.t('common.status'),
                  render: () => {
                    if (generating) {
                      return {
                        type: 'status',
                        value: {
                          label: i18n.t('performance_process.agreements.status.generating'),
                          status: 'info',
                        },
                      }
                    }

                    if (!canGenerate) {
                      const status = agreementStatus(agreement)
                      const label = agreementStatusLabel(status)
                      const variant = agreementStatusToVariant[status]

                      return {
                        type: 'status',
                        value: { label, status: variant },
                      }
                    }
                  },
                },
              ],
              itemDefinition: () => ({
                title: i18n.t('performance_process.agreements.page.title'),
                description: detailLabels.length > 0 ? detailLabels : undefined,
                avatar: {
                  type: 'file',
                  file: {
                    name: i18n.t('performance_process.agreements.page.title'),
                    type: 'pdf',
                  },
                },
              }),
            },
          },
        ]}
      />

      {openPreview && document && (
        <DocumentModal
          documentId={document.id.toString()}
          documents={[document]}
          onClose={() => setOpenPreview(false)}
        />
      )}
    </Box>
  )
}
